import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { useIntl } from "react-intl";
import moment from "moment";

import { isValidExcelDateString } from "helpers/dateTimeHelper";
import { IDefaultCurrencyOption } from "../../Types";
import getPackingFields from "./spreadsheetImportFields";
import { dangerousGoodsOptions, temperatureControlledOptions } from "../../PackingListDefaultOptions";

interface HandlingUnitOption {
  label: string;
  value: string;
}
interface ErrorObject {
  message: string;
  level: "error";
}

type HandlingUnitConfigs = {
  label: string;
  identifier: string;
  l_w_h: string;
  customs_value: boolean;
  additional_field: string;
  weight: string;
  stackable: boolean;
};

type Data<T> = Record<string, T>;
type RawData = any;

// Takes the handling units from the packing list config and converts them to options for the spreadsheet import
function convertHandlingUnitsToOptions(handlingUnits: Record<string, any>[]): HandlingUnitOption[] {
  return handlingUnits
    .filter((unit) => {
      const key = Object.keys(unit)[0];
      const { identifier } = unit[key];
      return !identifier.startsWith("custom_unit");
    })
    .map((unit) => {
      const key = Object.keys(unit)[0];
      const { label } = unit[key];
      return { label: label.toLowerCase(), value: key.toLowerCase() };
    });
}

function validateMandatoryField(
  rowData: any,
  rowConfig: any,
  field: string,
  errorMessage: string,
  addError: (field: string, error: ErrorObject) => void,
) {
  if (rowData.handling_unit === "container") return;
  if (
    (rowConfig[0].l_w_h === "mandatory" || rowConfig[0].weight === "mandatory") &&
    (rowData[field] === "" || rowData[field] === undefined)
  ) {
    addError(field, { message: errorMessage, level: "error" });
  }
}

function SpreadsheetImport({ packingListConfig, currencyOptions: curOptions, isOpen, onClose, setValue }: any) {
  const intl = useIntl();

  // All dropdown options used in the spreadsheet import for "select" values
  const handlingUnits = convertHandlingUnitsToOptions(packingListConfig?.handling_unit_options || []);
  const allHandlingUnitOptions = packingListConfig?.handling_unit_options
    ?.map((unit: any) => {
      const key = Object.keys(unit)[0];
      const options = unit[key].handling_unit_options;
      return options.map(({ key, value }: any) => ({ label: value, value: key }));
    })
    .flat()
    .filter(Boolean);
  const dangerousGoods = dangerousGoodsOptions(intl);
  const temperatureControl = temperatureControlledOptions(intl);
  const nmfcOptions = packingListConfig?.nmfc_values?.map(([value]: [string]) => ({
    value: parseFloat(value),
    label: value,
  }));
  const currencyOptions = [
    { label: "Not available", value: "" },
    ...(curOptions?.map((currency: IDefaultCurrencyOption) => ({
      label: currency.name,
      value: currency.code,
    })) || []),
  ];

  const selectOptions = {
    handlingUnits,
    allHandlingUnitOptions,
    nmfcOptions,
    dangerousGoods,
    temperatureControl,
    currencyOptions,
  };

  const fields = getPackingFields(packingListConfig, selectOptions);

  const onSubmit = (data: Data<RawData>) => {
    data.validData.forEach((item: any, key: number) => {
      // Find the matching handling unit details
      const matchingData = Object.values(
        packingListConfig?.handling_unit_options.find((unit: any) => Object.keys(unit)[0] === item.handling_unit),
      ) as HandlingUnitConfigs[];

      const { customs_value } = matchingData[0];

      // If customs info is filled in, include good_customs_information in the packing list item
      const customsEnabled = ["hs_code", "value", "currency_code"].every(
        (field) => item[field] !== "" && item[field] !== undefined,
      );

      // Format date correctly
      const parsedDate = moment(item.crating_required_by, ["DD-MM-YYYY", "D-M-YY", "DD-MM-YY", "DD-MM-YYYY", "YYYY-MM-DD"]);
      const formattedDate = parsedDate.format("YYYY-MM-DD");

      setValue(`packing_list.${key}`, {
        ...item,
        crating_required: item.crating_required_by !== "" && item.crating_required_by !== undefined,
        is_dangerous_good: item.dangerous_good_value !== "" && item.dangerous_good_value !== undefined,
        temperature_control_required: item.temperature_control_value !== "" && item.temperature_control_value !== undefined,
        m_unit: "cm",
        w_unit: "kg",
        index: key,
        ...(isValidExcelDateString(formattedDate) && { crating_required_by: formattedDate }),
        ...(item.container_unit && item.handling_unit === "container" && { container_type: item.container_unit }),
        ...(customsEnabled &&
          customs_value && {
            good_customs_information: [
              {
                hs_code: item.hs_code,
                value: item.value,
                currency_code: item.currency_code,
                description: item.description,
              },
            ],
          }),
      });
    });
  };

  return (
    <>
      {isOpen && (
        <ReactSpreadsheetImport
          isOpen={isOpen}
          onClose={onClose}
          fields={fields}
          onSubmit={onSubmit}
          autoMapSelectValues
          isNavigationEnabled
          uploadStepHook={async (data) => {
            // Upon uploading the file, convert any values within checkbox columns to TRUE.
            const checkboxValues = ["Stackable", "Hazardous"];
            const updatedData = data.map((row, rowIndex) => {
              if (rowIndex === 0) return row;
              const updatedRow = row.map((value, index) => {
                const header: any = data[0][index]; // Get the header for the current column
                if (checkboxValues.includes(header) && value !== null && value !== "") return "TRUE";
                return value;
              });
              return updatedRow;
            });

            // Set all handling units to lowercase for auto-mapping
            const modifiedData = updatedData.map((item) => [item[0]?.toLowerCase(), ...item.slice(1)]);
            return modifiedData;
          }}
          rowHook={(data, addError) => {
            // Find the handling unit config settings for the current row
            const rowConfig: any = Object.values(
              packingListConfig?.handling_unit_options.find((unit: any) => Object.keys(unit)[0] === data.handling_unit),
            ) as HandlingUnitConfigs[];

            validateMandatoryField(data, rowConfig, "length", "Required", addError);
            validateMandatoryField(data, rowConfig, "width", "Required", addError);
            validateMandatoryField(data, rowConfig, "height", "Required", addError);
            validateMandatoryField(data, rowConfig, "weight_piece", "Required", addError);

            // Throw an error if any of the number fields are not a valid number
            const numberFields = ["length", "width", "height", "units", "weight_piece"];
            numberFields.forEach((field) => {
              if (data[field]) {
                if (data[field] !== undefined && typeof data[field] === "string" && /[a-zA-Z]/.test(data[field] as string)) {
                  addError(field, { message: "Not a valid number", level: "error" });
                }
              }
            });

            // Throw an error if the date is not a valid date
            if (data.crating_required_by) {
              if (isValidExcelDateString(data.crating_required_by as string) === false) {
                addError("crating_required_by", { message: "Not a valid date", level: "error" });
              }
            }

            // Throw a required error if any of the customs info fields are filled, but not all of them. (All or nothing)
            const customFields = ["hs_code", "value", "currency_code", "description"];
            const filledCustomFields = customFields.filter((field) => !!data[field]);
            if (filledCustomFields.length > 0) {
              customFields.forEach((field) => {
                if (!data[field]) {
                  addError(field, {
                    message: "This field is required",
                    level: "error",
                  });
                }
              });
            }

            return data;
          }}
          customTheme={{
            components: {
              Button: {
                baseStyle: {
                  borderRadius: "none",
                },
              },
            },
            colors: {
              highlight: "#298CDE",
              background: "white",
              secondaryBackground: "#eef7ff",
              border: "#daefff",
              rsi: {
                50: "#eef7ff",
                100: "#82B9FB",
                200: "#82B9FB",
                300: "#298CDE",
                400: "#298CDE",
                500: "#257DC8",
                600: "#257DC8",
                700: "#0071CE",
                800: "#1D6DAF",
                900: "#155E99",
              },
            },
          }}
        />
      )}
    </>
  );
}

export default SpreadsheetImport;
